import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Smartphone } from 'lucide-react';

import welcomePageImg from '../../../../../assets/App_Img/Welcome_page.png';
import homePageImg from '../../../../../assets/App_Img/Home_page.png';
import itineraryPageImg from '../../../../../assets/App_Img/Itinerary_page.png';
import hotelBookingPageImg from '../../../../../assets/App_Img/HotelBooking_page.png';
import dinningPageImg from '../../../../../assets/App_Img/Dinning_page.png';
import activityPageImg from '../../../../../assets/App_Img/Activity_page.png';
import shopPageImg from '../../../../../assets/App_Img/Shop_page.png';
import thingsToCarryPageImg from '../../../../../assets/App_Img/ThingsToCarry_page.png';
import safetyTipPageImg from '../../../../../assets/App_Img/SafetyTip_page.png';
import profilePageImg from '../../../../../assets/App_Img/Profile_page.png';

import dashboardData from '../../../../../data/dashboard/dashboardData.json';

const travellerAppSlides = dashboardData.travellerAppSlides || [];

// Pure Fade-in & Fade-out Transition
const slideVariants = {
  enter: {
    opacity: 0
  },
  center: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeInOut'
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.35,
      ease: 'easeInOut'
    }
  }
};

const IMAGE_MAP = {
  profilePageImg,
  welcomePageImg,
  homePageImg,
  itineraryPageImg,
  hotelBookingPageImg,
  dinningPageImg,
  activityPageImg,
  shopPageImg,
  thingsToCarryPageImg,
  safetyTipPageImg,
};

const TravellerAppView = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef(null);

  // 10 Real Mobile App Screen Carousel Slides from JSON
  const slides = travellerAppSlides.map((slide) => ({
    ...slide,
    image: IMAGE_MAP[slide.imageKey] || null,
  }));

  // Auto slide effect
  useEffect(() => {
    if (!isAutoPlaying) return;
    autoPlayRef.current = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 4500);

    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [isAutoPlaying, slides.length]);

  return (
    <div
      data-tour="appview"
      className="w-full xl:col-span-4 flex flex-col items-center justify-center pt-4 xl:pt-0 space-y-3"
    >
      {/* Top Header & Subcaption */}
      <div className="text-center space-y-1">
        <div className="flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <Smartphone size={14} className="text-[#067978]" />
          <span>Traveller's App View</span>
        </div>

        <p className="text-xs sm:text-sm font-semibold text-[#067978] leading-tight pt-0.5">
          Only your Brand appears here.
        </p>
      </div>

      {/* Sleek Ultra-Thin Minimalist Mobile Phone Frame */}
      <div className="relative flex items-center justify-center">
        {/* Ultra-Thin Minimalist Phone Chassis (Proportional & Compact) */}
        <div
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
          className="w-[185px] xs:w-[195px] sm:w-[205px] h-[370px] xs:h-[390px] sm:h-[410px] bg-slate-900 rounded-[28px] p-[2.5px] shadow-lg border border-slate-700/60 relative overflow-hidden group shrink-0"
        >
          {/* Inner Screen Display Container (100% Edge-to-Edge) */}
          <div className="rounded-[25px] overflow-hidden bg-slate-950 w-full h-full relative">
            {/* Animated Interactive Slide Content with Pure Fade Transition */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSlide}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="w-full h-full absolute inset-0"
              >
                {slides[activeSlide].image ? (
                  <img
                    src={slides[activeSlide].image}
                    alt={slides[activeSlide].title}
                    className="w-full h-full object-cover object-top select-none pointer-events-none"
                  />
                ) : (
                  slides[activeSlide].render?.()
                )}
              </motion.div>
            </AnimatePresence>

            {/* Slide Navigation Overlay Controls on Hover */}
            <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 flex items-center justify-between z-30 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
                }}
                className="w-5 h-5 rounded-full bg-black/70 backdrop-blur-md text-white flex items-center justify-center pointer-events-auto hover:bg-black transition-colors shadow-xs cursor-pointer"
                aria-label="Previous Slide"
              >
                <ChevronLeft size={12} />
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveSlide((prev) => (prev + 1) % slides.length);
                }}
                className="w-5 h-5 rounded-full bg-black/70 backdrop-blur-md text-white flex items-center justify-center pointer-events-auto hover:bg-black transition-colors shadow-xs cursor-pointer"
                aria-label="Next Slide"
              >
                <ChevronRight size={12} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Screen Title Styled to Match Theme */}
      <div className="text-center pt-0.5 min-h-[36px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSlide}
            initial={{ opacity: 0, y: 2 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -2 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="flex flex-col items-center"
          >
            <span className="text-xs sm:text-[13px] font-medium text-[#064242] tracking-tight">
              {slides[activeSlide].title}
            </span>
            {slides[activeSlide].subtitle && (
              <span className="text-[10px] text-slate-400 font-normal mt-0.5">
                {slides[activeSlide].subtitle}
              </span>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TravellerAppView;
